import { Directive, computed, inject, signal } from '@angular/core';
import { AppSetupApi } from 'api/app-setup/app-setup-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { browserHelper } from 'lib/helpers/browser-helper.class.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { NavigateResult } from 'lib/services/navigate.service.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { FormLogHandler } from 'lib/ui/form/form-save-button.component.js';
import { IconType } from 'lib/ui/icons/index.js';
import { SideMenuService } from 'lib/ui/menu/side-menu.service.js';
import { TagListItem } from 'lib/ui/tag/tag-list.component.js';
import { IImage } from 'lib/ui/ui-models.js';
import { CoreComponent } from '../../../lib/ui/core/core.component.js';
import { environment } from '../environments/environment.js';
import { ExtendedFeed } from '../layout/services/layout-service.js';
import { ITypeRouteAction } from '../pages/type-routes/type-route.models.js';
import { IAppRoutes } from '../routes/app-routes.js';
import { CoreAppComponentDependencies } from './core-app-dependencies.js';

type LayoutType = 'mobile' | 'desktop';
interface ExtendedBrandLogo extends IImage {
	readonly title: string;
}

export interface IUserMenuItem {
	readonly title: string;
	readonly click: () => void;
	readonly routerLink?: string;
	readonly iconType?: IconType;
}

@Directive()
export abstract class CoreAppComponent extends CoreComponent {
	protected readonly dependencies: CoreAppComponentDependencies;

	protected readonly originalAppName = signal<string>(environment.appName);
	protected readonly defaultSquareImageCrop: IFileFormFieldCropOptions;
	protected readonly enableAvatarEditInFormComponents = false;

	protected readonly themeTags = computed<TagListItem[]>(() => {
		const tags: TagListItem[] = [
			{
				text: 'Light',
				tagType: this.theme() === 'light' ? 'primary' : 'basic',
				click: () => {
					this.dependencies.themeService.setTheme('light');
				}
			},
			{
				text: 'Dark',
				tagType: this.theme() === 'dark' ? 'primary' : 'basic',
				click: () => {
					this.dependencies.themeService.setTheme('dark');
				}
			}
		];

		return tags;
	});

	public readonly feeds = computed<readonly ExtendedFeed[]>(() => {
		return this.dependencies.layoutService.feeds();
	});

	public readonly unreadFeedsCount = computed<number>(() => {
		return this.dependencies.layoutService.unreadFeedsCount();
	});

	protected readonly appName = computed<string>(() => {
		const subdomain = this.registeredSubdomain();

		if (subdomain?.brandName) {
			return subdomain.brandName;
		}

		const brandedAppName = this.appContext()?.branding?.brandName;
		if (brandedAppName) {
			return brandedAppName;
		}

		return this.originalAppName();
	});

	protected readonly appTextLogos = computed<{ readonly dark: IImage; readonly light: IImage } | undefined>(() => {
		const light = this.appContext()?.appSetup.textLogoLight?.[0]?.image;
		const dark = this.appContext()?.appSetup.textLogoDark?.[0]?.image;

		if (light && dark) {
			return {
				dark,
				light
			};
		}

		return undefined;
	});

	protected readonly formLogHandler = signal<FormLogHandler>((data) =>
		this.dependencies.logService.logError({
			email: this.authUser()?.email,
			message: data.message,
			stacktrace: data.stacktrace,
			behavior: undefined
		})
	);

	protected readonly dashboardUrl = computed(() => {
		const authUser = this.authUser();
		if (authUser) {
			return this.dependencies.navigateService.dashboardPage(authUser.userType).getUrl();
		}

		return this.dependencies.navigateService.dashboardPage(undefined).getUrl();
	});

	protected readonly customSubdomainAbsoluteUrl = computed<string | undefined>(() => {
		const branding = this.appContext()?.branding;

		if (!branding?.brandingSubdomain) {
			return undefined;
		}

		const mainDomain = new URL(environment.productionUrl).hostname;

		return `https://${branding.brandingSubdomain}.${mainDomain}`;
	});

	protected serverSubdomain = signal<string | undefined>(undefined);
	protected browserSubdomain = signal<string | undefined>(undefined);

	protected readonly registeredSubdomain = computed<AppSetupApi.IAppContextSubdomain | undefined>(() => {
		const currentSubdomain = this.serverSubdomain() ?? this.browserSubdomain();

		if (!currentSubdomain) {
			return undefined;
		}

		const appContext = this.appContext();
		const registeredSubdomain = appContext?.subdomains.find(
			(m) => m.subdomain.toLowerCase() === currentSubdomain.toLowerCase()
		);

		if (!registeredSubdomain) {
			return undefined;
		}

		return registeredSubdomain;
	});

	protected readonly subdomainLogo = computed<ExtendedBrandLogo | undefined>(() => {
		const registeredSubdomain = this.registeredSubdomain();
		const brandLogo = registeredSubdomain?.logo?.[0]?.image;

		if (!brandLogo) {
			return undefined;
		}

		return {
			...brandLogo,
			title: registeredSubdomain.brandName ?? this.originalAppName()
		};
	});

	protected readonly brandLogoImage = computed<ExtendedBrandLogo | undefined>(() => {
		const subdomainLogo = this.subdomainLogo();

		if (subdomainLogo) {
			return subdomainLogo;
		}

		const branding = this.appContext()?.branding;
		const brandLogo = branding?.brandLogo?.[0]?.image;
		if (brandLogo) {
			return {
				...brandLogo,
				title: branding?.brandName ?? this.originalAppName()
			};
		}

		const appSetup = this.appSetup();

		const appSetupLogo = appSetup?.logo?.[0]?.image;
		if (appSetupLogo) {
			return {
				...appSetupLogo,
				title: branding?.brandName ?? this.originalAppName()
			};
		}

		return undefined;
	});

	protected readonly sideMenuService: SideMenuService = inject(SideMenuService);
	protected readonly menuIds = this.sideMenuService.menuIds;

	protected readonly layoutType = computed<LayoutType>(() => {
		if (this.isMobileView()) {
			return 'mobile';
		}
		return 'desktop';
	});

	protected readonly primaryUserMenuItems = computed<readonly IUserMenuItem[]>(() => {
		const menuItems: IUserMenuItem[] = [
			{
				title: 'Settings',
				click: () => {},
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.profileRoutes.view.children.edit)
					.getUrl()
			},
			{
				title: 'Notifications',
				click: () => {},
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.profileRoutes.view.children.notifications)
					.getUrl()
			}
		];

		const authUser = this.authUser();
		if (authUser?.userType === 'trainer') {
			menuItems.push({
				title: 'Subscription',
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.profileRoutes.view.children.subscription)
					.getUrl(),
				click: () => {}
			});
		}

		menuItems.push({
			title: 'Install app',
			routerLink: this.dependencies.navigateService
				.actionRoute((routes) => routes.profileRoutes.utilities.children.install)
				.getUrl(),
			click: () => {}
		});

		if (authUser) {
			menuItems.push({
				title: 'Helpdesk',
				routerLink: this.dependencies.navigateService
					.actionRoute((routes) => routes.helpdeskRoutes.conversation.children.form, authUser.id)
					.getUrl(),
				click: () => {}
			});
		}
		return menuItems;
	});

	protected readonly secondaryUserMenuItems = computed<readonly IUserMenuItem[]>(() => [
		{
			title: 'Log out',
			iconType: 'logout',
			routerLink: this.dependencies.navigateService.logoutPage().getUrl(),
			click: () => {}
		}
	]);

	public readonly notificationsRouterLink: string;
	public readonly avatarRouterLink: string;

	constructor() {
		super();

		const dependencies = inject(CoreAppComponentDependencies);

		this.dependencies = dependencies;
		this.defaultSquareImageCrop = dependencies.coreAppService.defaultSquareImageCrop;

		this.notificationsRouterLink = this.actionRoute(
			(routes) => routes.profileRoutes.view.children.notifications
		).getUrl();

		this.avatarRouterLink = this.actionRoute((routes) => routes.profileRoutes.view.children.avatar).getUrl();

		this.serverSubdomain.set(this.dependencies.requestService.getSubdomainFromServer());
		this.browserSubdomain.set(browserHelper.getCurrentSubdomain());
	}

	protected openPrimaryMenu(): void {
		this.sideMenuService.openSideMenu({ menuId: this.menuIds.primaryMenuId });
	}

	protected closePrimaryMenu(): void {
		this.sideMenuService.closeSideMenu({ menuId: this.menuIds.primaryMenuId });
	}

	protected closeAllMenus(): void {
		this.sideMenuService.closeAll();
	}

	protected actionRoute(
		actionSelection: (appRoutes: IAppRoutes) => ITypeRouteAction<any>,
		primaryId?: string,
		secondaryId?: string,
		tertiaryId?: string
	): NavigateResult {
		return this.dependencies.navigateService.actionRoute(actionSelection, primaryId, secondaryId, tertiaryId);
	}

	protected getOptionalItem<T extends CosmosDb.ICosmosItem>(
		id: string,
		linkedItems: readonly CosmosDb.ICosmosItem[]
	): T | undefined {
		return this.dependencies.linkedItemsService.getOptionalItem(id, linkedItems);
	}

	protected getRequiredItemId(item: CosmosDb.ICosmosItem | undefined): string {
		return item?.id ?? guidHelper.newGuid();
	}

	protected getRequiredItemName(name: string | undefined): string {
		return stringHelper.getRequiredItemName(name);
	}
}
